import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Coffee, Bell, CheckCircle2, AlertCircle, Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ToastNotification = forwardRef(({ notification, onClose }, ref) => {
  const { t } = useTranslation('common');
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const duration = 5000;
    const interval = 50;
    const step = (interval / duration) * 100;

    const progressTimer = setInterval(() => {
      setProgress(prev => {
        const next = prev - step;
        if (next <= 0) {
          clearInterval(progressTimer);
          return 0;
        }
        return next;
      });
    }, interval);

    const closeTimer = setTimeout(() => {
      onClose();
    }, duration);

    return () => {
      clearInterval(progressTimer);
      clearTimeout(closeTimer);
    };
  }, [onClose]);

  const getConfig = () => {
    switch (notification.type) {
      case 'calendar':
        return {
          icon: <Calendar className="w-4 h-4" />,
          color: '#3d8ef0',
          iconBg: 'bg-[#2563eb]/15',
          iconColor: 'text-[#3d8ef0]',
          progressColor: 'bg-[#2563eb]',
        };
      case 'break':
        return {
          icon: <Coffee className="w-4 h-4" />,
          color: '#f97316',
          iconBg: 'bg-orange-500/15',
          iconColor: 'text-orange-400',
          progressColor: 'bg-orange-400',
        };
      case 'success':
        return {
          icon: <CheckCircle2 className="w-4 h-4" />,
          color: '#10b981',
          iconBg: 'bg-emerald-500/15',
          iconColor: 'text-emerald-400',
          progressColor: 'bg-emerald-400',
        };
      case 'error':
        return {
          icon: <AlertCircle className="w-4 h-4" />,
          color: '#ef4444',
          iconBg: 'bg-red-500/15',
          iconColor: 'text-red-400',
          progressColor: 'bg-red-400',
        };
      case 'plus':
        return {
          icon: <Crown className="w-4 h-4" />,
          color: '#f59e0b',
          iconBg: 'bg-amber-500/15',
          iconColor: 'text-amber-400',
          progressColor: 'bg-gradient-to-r from-amber-400 to-yellow-400',
        };
      default:
        return {
          icon: <Bell className="w-4 h-4" />,
          color: '#7a96b0',
          iconBg: 'bg-white/5',
          iconColor: 'text-[var(--color-text-secondary)]',
          progressColor: 'bg-[#2563eb]',
        };
    }
  };

  const config = getConfig();
  const isPlusNotification = notification.type === 'plus';

  const handleClick = () => {
    if (isPlusNotification) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('notesplus-open-plus-promo-modal'));
      }
      onClose();
    }
  };

  return (
    <motion.div
      initial={{ x: 380, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 380, opacity: 0 }}
      transition={{ type: 'spring', damping: 28, stiffness: 320 }}
      className={`relative w-[340px] bg-[var(--color-bg-elevated)] border border-white/[0.08] rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.4)] overflow-hidden mb-2 ${isPlusNotification ? 'cursor-pointer' : ''}`}
      onClick={isPlusNotification ? handleClick : undefined}
    >
      {/* Sol renkli şerit */}
      <div
        className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-xl"
        style={{ backgroundColor: config.color }}
      />

      {/* İçerik */}
      <div className="flex items-start gap-3 px-4 py-3 pl-5">
        {/* İkon */}
        <div className={`flex-shrink-0 w-8 h-8 rounded-lg ${config.iconBg} flex items-center justify-center ${config.iconColor}`}>
          {config.icon}
        </div>

        {/* Metin */}
        <div className="flex-1 min-w-0 pt-0.5">
          <p className="text-sm font-semibold text-[var(--color-text-primary)] leading-snug">
            {notification.title}
          </p>
          {notification.message && (
            <p className="text-xs text-[#8aaac8] mt-0.5 line-clamp-2 leading-relaxed">
              {notification.message}
            </p>
          )}
        </div>

        {/* Kapat butonu */}
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="flex-shrink-0 p-1 rounded-md text-[#5a7a9a] hover:text-[var(--color-text-secondary)] hover:bg-white/5 transition-colors mt-0.5"
          aria-label={t('close')}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-[2px] bg-white/5">
        <div
          className={`h-full ${config.progressColor} transition-none`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </motion.div>
  );
});

ToastNotification.displayName = 'ToastNotification';

const ToastNotificationContainer = () => {
  const [visibleToasts, setVisibleToasts] = useState([]);
  const lastNotificationRef = useRef(null);
  const notificationTimeoutRef = useRef(null);

  useEffect(() => {
    const handleToastEvent = (event) => {
      try {
        const { type, title, message } = event.detail || {};
        if (type && title) {
          const notificationKey = `${type}-${title}-${message}`;

          if (lastNotificationRef.current === notificationKey) {
            if (notificationTimeoutRef.current) clearTimeout(notificationTimeoutRef.current);
            notificationTimeoutRef.current = setTimeout(() => {
              lastNotificationRef.current = null;
            }, 2000);
            return;
          }

          lastNotificationRef.current = notificationKey;
          const toastNotification = {
            id: `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            type,
            title,
            message,
            timestamp: new Date().toISOString(),
          };

          setVisibleToasts((prev) => {
            if (prev.find((t) => t.id === toastNotification.id)) return prev;
            return [toastNotification, ...prev];
          });

          if (notificationTimeoutRef.current) clearTimeout(notificationTimeoutRef.current);
          notificationTimeoutRef.current = setTimeout(() => {
            lastNotificationRef.current = null;
          }, 2000);
        }
      } catch (error) {
        console.error('Toast event işlenirken hata:', error);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('notesplus-show-toast', handleToastEvent);
    }
    return () => {
      if (notificationTimeoutRef.current) clearTimeout(notificationTimeoutRef.current);
      if (typeof window !== 'undefined') {
        window.removeEventListener('notesplus-show-toast', handleToastEvent);
      }
    };
  }, []);

  const handleClose = (id) => {
    setVisibleToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <div className="fixed bottom-6 right-6 z-[10001] pointer-events-none">
      <div className="pointer-events-auto">
        <AnimatePresence mode="popLayout">
          {visibleToasts.map((notification) => (
            <ToastNotification
              key={notification.id}
              notification={notification}
              onClose={() => handleClose(notification.id)}
            />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ToastNotificationContainer;
